import React from 'react';

import IProductInstallationCategory from 'api/interfaces/products/IProductInstallationCategory';
import translations from 'services/translations/translations';
import sortByTranslation from 'services/sort/sortByTranslation';
import {
  ICategoryChoices,
  INSTALLATION_CATEGORY,
  OTHER_EXPENSE_CATEGORY,
  getChoiceList,
} from 'services/offers/offerChoices';

const getFilteredCategories = (
  installationCategories: IProductInstallationCategory[],
  type?: string
): string[] => {
  if (type === undefined) {
    return installationCategories.map(
      (installationCategory) => installationCategory.category
    );
  }

  return installationCategories
    .filter((installationCategory) => installationCategory.type === type)
    .map((installationCategory) => installationCategory.category);
};

// The choice lists of the installations and the other expenses (installationTypes)
const listsOfType = (type?: string): string[] => {
  if (type === 'installation') {
    return [INSTALLATION_CATEGORY];
  }
  if (type === 'other-expense') {
    return [OTHER_EXPENSE_CATEGORY];
  }
  return [INSTALLATION_CATEGORY, OTHER_EXPENSE_CATEGORY];
};

const getInstallationCategoryOptions = (
  installationCategories: IProductInstallationCategory[],
  t: any,
  type?: string,
  // The choice lists of the admins: their names and order, without the hidden ones except
  // the current category of the row
  categoryChoices?: ICategoryChoices & { current?: null | string }
): JSX.Element[] => {
  const categoriesTranslationLocation = `${translations.products}:installationCategories`;
  const lists = listsOfType(type);

  if (
    categoryChoices !== undefined &&
    categoryChoices.choices.some((choice) => lists.includes(choice.list))
  ) {
    // The current category is kept in the list it belongs to, an unknown one in the first
    const listOfCurrent =
      lists.find((list) =>
        categoryChoices.choices.some(
          (choice) =>
            choice.list === list && choice.value === categoryChoices.current
        )
      ) ?? lists[0];

    return lists
      .flatMap((list) =>
        getChoiceList(
          categoryChoices.choices,
          list,
          list === listOfCurrent ? categoryChoices.current : null,
          categoryChoices.language
        )
      )
      .map((option) => (
        <option
          key={`installation-category-${option.value}`}
          value={option.value}
        >
          {option.label}
        </option>
      ));
  }

  const getOption = (installationCategory: string) => (
    <option
      key={`installation-category-${installationCategory}`}
      value={installationCategory}
    >
      {t(`${categoriesTranslationLocation}:${installationCategory}`, {
        defaultValue: installationCategory,
      })}
    </option>
  );

  const categoriesSortedByTranslation = getFilteredCategories(
    installationCategories,
    type
  ).sort(sortByTranslation(t, categoriesTranslationLocation));

  return categoriesSortedByTranslation.map((installationCategory: string) =>
    getOption(installationCategory)
  );
};

export default getInstallationCategoryOptions;
